import { logger } from '@moonpig/web-core-monitoring'
import { EppoExperimentOverrides } from '@moonpig/web-core-utils/src/eppoContext/EppoContext'
import { EppoClient, Flag, VariationType } from '@eppo/js-client-sdk-common'
import { DefaultSubjectAttributes } from '../../shared/DefaultSubjectAttributes'
import { setupGetAllExperimentVariants } from '../setupGetAllExperimentVariants/setupGetAllExperimentVariants'
import {
  GetExperiment,
  setupGetExperimentVariant,
} from '../setupGetExperimentVariant/setupGetExperimentVariant'

type SetupEppo = {
  sessionId: string
  defaultAttributes: DefaultSubjectAttributes
  overrides: EppoExperimentOverrides
  client: EppoClient | null
}

export const setupEppo = async ({
  sessionId,
  defaultAttributes,
  overrides,
  client,
}: SetupEppo) => {
  const {
    getExperimentString,
    getExperimentBoolean,
    getExperimentJson,
    getExperimentInteger,
    getExperimentNumeric,
    trackedServerExperiments,
  } = setupGetExperimentVariant({
    client,
    subjectKey: sessionId,
    defaultAttributes,
    overrides,
  })

  const getAllExperimentVariants = setupGetAllExperimentVariants({
    client,
    subjectKey: sessionId,
    defaultAttributes,
    overrides,
  })

  const getExperiment: GetExperiment = (experimentKey, args) => {
    const clientConfiguration = client?.getFlagConfigurations()
    if (!clientConfiguration) return args.fallback
    const config = clientConfiguration[experimentKey] as Flag
    if (!config) return args.fallback

    switch (config.variationType) {
      case VariationType.STRING:
        return getExperimentString(experimentKey, args)
      case VariationType.BOOLEAN:
        return getExperimentBoolean(experimentKey, args)
      case VariationType.JSON:
        return getExperimentJson(experimentKey, args)
      case VariationType.INTEGER:
        return getExperimentInteger(experimentKey, args)
      case VariationType.NUMERIC:
        return getExperimentNumeric(experimentKey, args)
      default:
        logger.fixToday(
          `Unknown variation type "${config.variationType}" for experiment "${experimentKey}"`,
        )
        return args.fallback
    }
  }

  const getFlag: GetExperiment = (flagKey, args) => {
    const nonLoggingArgs = {
      ...args,
      subjectAttributes: { ...args.subjectAttributes, shouldLog: false },
    }
    return getExperiment(flagKey, nonLoggingArgs)
  }

  return {
    getExperiment,
    getFlag,
    getAllExperimentVariants,
    trackedServerExperiments,
  }
}
